import { ApiError } from "@platform/core/api";

import {
	WEBHOOK_EVENT_TYPES,
	WEBHOOK_RUN_KINDS,
	type WebhookDelivery,
	type WebhookEndpoint,
	type WebhookEndpointChange,
	type WebhookEndpointFields,
	type WebhookRunKind,
} from "./webhooks.types";

// A first check only. The server also refuses ports other than 443, IP addresses and names that
// resolve to a private address, which a browser cannot see.
export function isHttpsUrl(value: string): boolean {
	try {
		const url = new URL(value.trim());
		return url.protocol === "https:" && url.hostname.length > 0;
	} catch {
		return false;
	}
}

// Every kind chosen is sent as null, so a kind added later reaches the endpoint too.
export function runKindsPayload(kinds: readonly WebhookRunKind[]): WebhookRunKind[] | null {
	return WEBHOOK_RUN_KINDS.every((kind) => kinds.includes(kind)) ? null : [...kinds];
}

export const NEW_ENDPOINT_FIELDS: WebhookEndpointFields = {
	url: "",
	description: "",
	eventTypes: [...WEBHOOK_EVENT_TYPES],
	runKinds: [...WEBHOOK_RUN_KINDS],
};

export function endpointFields(endpoint: WebhookEndpoint): WebhookEndpointFields {
	return {
		url: endpoint.url,
		description: endpoint.description ?? "",
		eventTypes: [...endpoint.eventTypes],
		runKinds: [...(endpoint.runKinds ?? WEBHOOK_RUN_KINDS)],
	};
}

function sameSet<T>(a: readonly T[], b: readonly T[]): boolean {
	return a.length === b.length && a.every((value) => b.includes(value));
}

// Only what the form changed. An untouched URL is not checked again, and an untouched list
// written through the API keeps its form, so every kind listed does not turn into null.
export function endpointChange(
	endpoint: WebhookEndpoint,
	fields: WebhookEndpointFields,
): WebhookEndpointChange {
	const before = endpointFields(endpoint);
	const change: WebhookEndpointChange = {};
	const url = fields.url.trim();
	if (url !== endpoint.url) change.url = url;
	const description = fields.description.trim();
	if (description !== (endpoint.description ?? "")) change.description = description || null;
	if (!sameSet(fields.eventTypes, before.eventTypes)) change.eventTypes = [...fields.eventTypes];
	if (!sameSet(fields.runKinds, before.runKinds))
		change.runKinds = runKindsPayload(fields.runKinds);
	return change;
}

export type EndpointHealth = "enabled" | "failing" | "disabled" | "gone" | "disabledFailing";

export function endpointHealth(endpoint: WebhookEndpoint): EndpointHealth {
	if (endpoint.status === "disabled") {
		if (endpoint.disabledReason === "gone_410") return "gone";
		if (endpoint.disabledReason === "failing") return "disabledFailing";
		return "disabled";
	}
	return endpoint.failingSince ? "failing" : "enabled";
}

// The time the replaced secret stops signing, or null when no rotation overlaps now.
export function rotationEndsAt(endpoint: WebhookEndpoint, now = Date.now()): string | null {
	const at = endpoint.previousSecretExpiresAt;
	return at && new Date(at).getTime() > now ? at : null;
}

// An endpoint by its host, which fits beside a status in a table cell.
export function endpointHost(url: string): string {
	try {
		return new URL(url).host;
	} catch {
		return url;
	}
}

// The receiver's answer in one word: its status code, or why there was none.
export function lastResponse(delivery: Pick<WebhookDelivery, "lastStatusCode" | "lastErrorCode">) {
	if (delivery.lastStatusCode !== null) return String(delivery.lastStatusCode);
	return delivery.lastErrorCode ?? "-";
}

export type WebhookErrorKey =
	| "urlNotAllowed"
	| "endpointLimit"
	| "billingInactive"
	| "endpointDisabled"
	| "testPending"
	| "redeliveryPending"
	| "tooSoon"
	| "unavailable"
	| "generic";

const ERROR_KEYS: Record<string, WebhookErrorKey> = {
	webhook_url_not_allowed: "urlNotAllowed",
	webhook_endpoint_limit: "endpointLimit",
	webhook_endpoint_disabled: "endpointDisabled",
	webhook_test_pending: "testPending",
	webhook_redelivery_pending: "redeliveryPending",
	webhook_test_too_soon: "tooSoon",
	webhook_redelivery_too_soon: "tooSoon",
	webhooks_unavailable: "unavailable",
};

// The server's messages are English, so the page shows its own text for every answer it expects.
export function errorKey(error: unknown): WebhookErrorKey {
	if (!(error instanceof ApiError)) return "generic";
	if (error.type === "payment_required") return "billingInactive";
	return ERROR_KEYS[error.code] ?? "generic";
}

// Seconds the server asked to wait, when it named them.
export function retryAfter(error: unknown): number | null {
	if (!(error instanceof ApiError)) return null;
	const seconds = (error.details as { retry_after?: unknown } | undefined)?.retry_after;
	return typeof seconds === "number" ? seconds : null;
}
